class StoreDevice {
  constructor (
    id = undefined,
    name,
    phoneNumber,
    enabled,
    userId,
    typeId,
    statusId,
    coordinateIds,
    statusChangeCodeId,
    markerTypeId,
    isActive,
    attributeValueIds,
    modelId,
    inputIds,
    outputIds,
    phoneWssAppVersion,
    phoneAndroidSDKVersionNumber,
    phoneManufacturerName,
    phoneModel,
    phoneNetworkOperatorName,
    phoneDetectedPhoneNumber,
    uuidDevice
  ) {
    this.id = id
    this.name = name
    this.phoneNumber = phoneNumber
    this.enabled = enabled
    this.userId = userId
    this.user = null
    this.typeId = typeId
    this.type = null
    this.statusId = statusId
    this.status = null
    this.coordinateIds = coordinateIds
    this.coordinates = []
    this.statusChangeCodeId = statusChangeCodeId
    this.statusChangeCode = null
    this.markerTypeId = markerTypeId
    this.markerType = null
    this.isActive = isActive
    this.attributeValueIds = attributeValueIds
    this.attributeValues = []
    this.modelId = modelId
    this.model = null
    this.inputIds = inputIds
    this.inputs = []
    this.outputIds = outputIds
    this.outputs = []
    this.phoneWssAppVersion = phoneWssAppVersion
    this.phoneAndroidSDKVersionNumber = phoneAndroidSDKVersionNumber
    this.phoneManufacturerName = phoneManufacturerName
    this.phoneModel = phoneModel
    this.phoneNetworkOperatorName = phoneNetworkOperatorName
    this.phoneDetectedPhoneNumber = phoneDetectedPhoneNumber
    this.uuidDevice = uuidDevice
  }

  toNormalizedRESTModel () {
    const model = {
      // id is read-only
      name: this.name,
      phoneNumber: this.phoneNumber,
      enabled: this.enabled,
      // userId is read-only
      typeId: this.typeId,
      statusId: this.statusId,
      // coordinateIds is read-only
      // statusChangeCodeId is read-only
      markerTypeId: this.markerTypeId,
      // isActive is read-only
      // attributeValueIds is read-only
      attributeValues: this.attributeValues.map(attributeValue => attributeValue.toNormalizedRESTModel()),
      // inputIds is read-only
      inputs: this.inputs.map(deviceInput => deviceInput.toNormalizedRESTModel()),
      // outputIds is read-only
      outputs: this.outputs.map(deviceOutput => deviceOutput.toNormalizedRESTModel()),
      modelId: this.modelId
      // phoneWssAppVersion is read-only
      // phoneAndroidSDKVersionNumber is read-only
      // phoneManufacturerName is read-only
      // phoneModel is read-only
      // phoneNetworkOperatorName is read-only
      // phoneDetectedPhoneNumber is read-only
    }
    // Include tracker-specific fields if they exist
    if (this.uuid !== undefined) {
      model.uuid = this.uuid
    }
    if (this.password !== undefined) {
      model.password = this.password
    }
    if (this.familyId !== undefined) {
      model.familyId = this.familyId
    }
    if (this.licensePlate !== undefined) {
      model.licensePlate = this.licensePlate
    }
    return model
  }

  /** PUT /device/{id} — scalar device fields only (matches UpdateDeviceDto). */
  toDeviceUpdateRESTModel () {
    return {
      name: this.name,
      phoneNumber: this.phoneNumber,
      enabled: this.enabled,
      typeId: this.typeId,
      modelId: this.modelId,
      statusId: this.statusId,
      statusChangeCodeId: this.statusChangeCodeId,
      markerTypeId: this.markerTypeId,
      isActive: this.isActive,
      phoneWssAppVersion: this.phoneWssAppVersion,
      phoneAndroidSDKVersionNumber: this.phoneAndroidSDKVersionNumber,
      phoneManufacturerName: this.phoneManufacturerName,
      phoneModel: this.phoneModel,
      phoneNetworkOperatorName: this.phoneNetworkOperatorName,
      phoneDetectedPhoneNumber: this.phoneDetectedPhoneNumber
    }
  }

  /**
   * PUT /tracker/{id} — legacy parity: enabled toggle maps to isActive (enabled_device);
   * status radio maps to statusId (status_device). UpdateDeviceTrackerDto includes both
   * Enabled and IsActive; omitting IsActive defaults to false and clears login access.
   */
  toTrackerUpdateRESTModel () {
    const loginActive = this.isActive ?? this.enabled ?? true
    return {
      ...this.toNormalizedRESTModel(),
      enabled: loginActive,
      isActive: loginActive,
      statusId: this.statusId
    }
  }

  static FromNormalizedRESTModel ({
    id,
    name,
    phoneNumber,
    enabled,
    userId,
    typeId,
    statusId,
    coordinateIds,
    statusChangeCodeId,
    markerTypeId,
    isActive,
    attributeValueIds,
    modelId,
    inputIds,
    outputIds,
    phoneWssAppVersion,
    phoneAndroidSDKVersionNumber,
    phoneManufacturerName,
    phoneModel,
    phoneNetworkOperatorName,
    phoneDetectedPhoneNumber,
    uuidDevice,
    uuid,
    password,
    familyId,
    licensePlate,
    isDeleted,
    modelName,
    loginBlockedUntil,
    batteryPercent,
    gsmSignalStrength,
    gpsTrackingStatus,
    lastStatusUpdate
  }) {
    const device = new StoreDevice(
      id,
      name,
      phoneNumber,
      enabled,
      userId,
      typeId,
      statusId,
      coordinateIds,
      statusChangeCodeId,
      markerTypeId,
      isActive,
      attributeValueIds,
      modelId,
      inputIds,
      outputIds,
      phoneWssAppVersion,
      phoneAndroidSDKVersionNumber,
      phoneManufacturerName,
      phoneModel,
      phoneNetworkOperatorName,
      phoneDetectedPhoneNumber,
      uuidDevice
    )
    // Attach tracker-specific fields if they exist
    if (uuid !== undefined) {
      device.uuid = uuid
    }
    if (password !== undefined) {
      device.password = password
    }
    if (familyId !== undefined) {
      device.familyId = familyId
    }
    if (licensePlate !== undefined) {
      device.licensePlate = licensePlate
    }
    if (isDeleted !== undefined) {
      device.isDeleted = isDeleted
    }
    if (modelName !== undefined) {
      device.modelName = modelName
    }
    if (loginBlockedUntil !== undefined) {
      device.loginBlockedUntil = loginBlockedUntil
    }
    if (batteryPercent !== undefined) {
      device.batteryPercent = batteryPercent
    }
    if (gsmSignalStrength !== undefined) {
      device.gsmSignalStrength = gsmSignalStrength
    }
    if (gpsTrackingStatus !== undefined) {
      device.gpsTrackingStatus = gpsTrackingStatus
    }
    if (lastStatusUpdate !== undefined) {
      device.lastStatusUpdate = lastStatusUpdate
    }
    return device
  }

  toJSON () {
    const json = {
      id: this.id,
      name: this.name,
      phoneNumber: this.phoneNumber,
      enabled: this.enabled,
      userId: this.userId,
      typeId: this.typeId,
      statusId: this.statusId,
      coordinateIds: this.coordinateIds,
      statusChangeCodeId: this.statusChangeCodeId,
      markerTypeId: this.markerTypeId,
      isActive: this.isActive,
      attributeValueIds: this.attributeValueIds,
      modelId: this.modelId,
      inputIds: this.inputIds,
      outputIds: this.outputIds,
      phoneWssAppVersion: this.phoneWssAppVersion,
      phoneAndroidSDKVersionNumber: this.phoneAndroidSDKVersionNumber,
      phoneManufacturerName: this.phoneManufacturerName,
      phoneModel: this.phoneModel,
      phoneNetworkOperatorName: this.phoneNetworkOperatorName,
      phoneDetectedPhoneNumber: this.phoneDetectedPhoneNumber,
      uuidDevice: this.uuidDevice
    }
    // Include tracker-specific fields if they exist
    if (this.uuid !== undefined) {
      json.uuid = this.uuid
    }
    if (this.password !== undefined) {
      json.password = this.password
    }
    if (this.familyId !== undefined) {
      json.familyId = this.familyId
    }
    if (this.licensePlate !== undefined) {
      json.licensePlate = this.licensePlate
    }
    if (this.isDeleted !== undefined) {
      json.isDeleted = this.isDeleted
    }
    if (this.modelName !== undefined) {
      json.modelName = this.modelName
    }
    if (this.batteryPercent !== undefined) {
      json.batteryPercent = this.batteryPercent
    }
    if (this.gsmSignalStrength !== undefined) {
      json.gsmSignalStrength = this.gsmSignalStrength
    }
    if (this.gpsTrackingStatus !== undefined) {
      json.gpsTrackingStatus = this.gpsTrackingStatus
    }
    if (this.lastStatusUpdate !== undefined) {
      json.lastStatusUpdate = this.lastStatusUpdate
    }
    return json
  }

  static FromJSON ({
    id,
    name,
    phoneNumber,
    enabled,
    userId,
    typeId,
    statusId,
    coordinateIds,
    statusChangeCodeId,
    markerTypeId,
    isActive,
    attributeValueIds,
    modelId,
    inputIds,
    outputIds,
    phoneWssAppVersion,
    phoneAndroidSDKVersionNumber,
    phoneManufacturerName,
    phoneModel,
    phoneNetworkOperatorName,
    phoneDetectedPhoneNumber,
    uuidDevice,
    uuid,
    password,
    familyId,
    licensePlate,
    isDeleted,
    modelName,
    loginBlockedUntil,
    batteryPercent,
    gsmSignalStrength,
    gpsTrackingStatus,
    lastStatusUpdate
  }) {
    const device = new StoreDevice(
      id,
      name,
      phoneNumber,
      enabled,
      userId,
      typeId,
      statusId,
      coordinateIds,
      statusChangeCodeId,
      markerTypeId,
      isActive,
      attributeValueIds,
      modelId,
      inputIds,
      outputIds,
      phoneWssAppVersion,
      phoneAndroidSDKVersionNumber,
      phoneManufacturerName,
      phoneModel,
      phoneNetworkOperatorName,
      phoneDetectedPhoneNumber,
      uuidDevice
    )
    // Attach tracker-specific fields if they exist
    if (uuid !== undefined) {
      device.uuid = uuid
    }
    if (password !== undefined) {
      device.password = password
    }
    if (familyId !== undefined) {
      device.familyId = familyId
    }
    if (licensePlate !== undefined) {
      device.licensePlate = licensePlate
    }
    if (isDeleted !== undefined) {
      device.isDeleted = isDeleted
    }
    if (modelName !== undefined) {
      device.modelName = modelName
    }
    if (loginBlockedUntil !== undefined) {
      device.loginBlockedUntil = loginBlockedUntil
    }
    if (batteryPercent !== undefined) {
      device.batteryPercent = batteryPercent
    }
    if (gsmSignalStrength !== undefined) {
      device.gsmSignalStrength = gsmSignalStrength
    }
    if (gpsTrackingStatus !== undefined) {
      device.gpsTrackingStatus = gpsTrackingStatus
    }
    if (lastStatusUpdate !== undefined) {
      device.lastStatusUpdate = lastStatusUpdate
    }
    return device
  }
}

export default StoreDevice
